import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { trackInternalEvent, trackMarketingEvent } from '@rio/tracking'
import { useMutation } from '@tanstack/react-query'
import snakeCase from 'lodash/snakeCase'
import { GuestResumeContext } from 'builder/context/GuestResumeContext'
import { useI18n } from 'builder/hooks/useI18n'
import {
  ResumeFieldSuggest,
  SectionNames,
  selectors,
  UNBLOCKED_EDITOR_URL_PARAM,
} from 'builder/modules/resumeEditor'
import { useQueryParam } from 'builder/hooks/useQueryParam'
import { getScoreSuggestions } from 'builder/modules/resumeEditor/resumeScoreOracle'
import { PersonalDetailsSectionGuest } from 'builder/components/PersonalDetailsSectionGuest'
import { useSignUpCopyright } from 'builder/components/SignUpCopyright'
import { validateEmail } from 'builder/components/SimpleEmailForm/utils/validateEmail'
import { useDebouncedEffect } from 'builder/hooks/useDebouncedEffect'
import { apiClient } from 'builder/modules/apiClient'
import { navigate } from 'builder/modules/navigate'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import ErrorLogger from 'builder/services/ErrorLogger'
import { FetchStatuses } from 'builder/modules/constants'
import FocusManager from 'builder/services/FocusManager'
import { getRecaptchaLoginActionToken } from 'builder/utils/recapchaActions'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { sectionTitleTranslation } from '../ATSResumeEditor/builderSectionsConfig'
import { Container, GuestCopyrightContainer, GuestCopyrightContent } from './styles'
import {
  Container as MainContentContainer,
  ContentSectionWrapper,
} from './components/ContentSection/styles'
import { ResumeScoreContainer } from './components/ResumeScoreContainer/ResumeScoreContainer'
import { NavigationSection } from './components/NavigationSection/NavigationSection'
import { LOCALIZATION_PREFIX } from './constants'

const UNKNOWN_ERROR_MESSAGE = 'Unknown error'

export const StepByStepFlowGuest = () => {
  const {
    resume,
    toggleEmailRequiredError,
    setShowEmailRequiredError,
    setShouldFocusEmailField,
    resetResume,
  } = useContext(GuestResumeContext)
  const { i18n } = useI18n()
  const sectionNameCreator = sectionTitleTranslation(resume.template, i18n.t)
  const nextSectionName = sectionNameCreator(SectionNames.workExperiences)
  const nextButtonText = `${i18n.t(
    `${LOCALIZATION_PREFIX}.navigation_container.next_button_text`,
  )}: ${nextSectionName}`
  const { points: resumeScore, suggestions } = getScoreSuggestions(resume)
  const copyrightMarkup = useSignUpCopyright({ isEmailOnly: true })
  const abortControllerRef = useRef(new AbortController())
  const [isSigningUp, setIsSigningUp] = useState(false)
  const [backendValidationMessage, setBackendValidationMessage] = useState('')
  const [isConsentChecked, setIsConsentChecked] = useState(false)
  const [emailValidationStatus, setEmailValidationStatus] = useState(FetchStatuses.notAsked)
  const { isPhone } = useMediaQueries()
  const [paramMode] = useQueryParam('mode')

  const autoTailoringFromPublicJobBoard = useTypedSelector(
    selectors.autoTailoringFromPublicJobBoard,
  )

  const isAutoTailoringFromPublicJobBoard = useMemo(() => {
    return (
      paramMode === 'auto_tailoring_from_public_job_board' &&
      autoTailoringFromPublicJobBoard.enabled
    )
  }, [paramMode, autoTailoringFromPublicJobBoard])

  const scoreImprovementSuggestion: ResumeFieldSuggest = useMemo(() => {
    const requiredScore = 100 - resumeScore

    const item = suggestions?.find(item => {
      return item.sectionId === SectionNames.details
    })

    if (!item) return { text: '', points: 0, type: 'section', key: '' }

    return {
      fieldId: item.fieldId,
      type: item.type,
      text: item.text,
      points: Math.min(item.points, requiredScore),
      key: item.key,
      sectionName: item.sectionName,
    }
  }, [resumeScore, suggestions])

  const { mutateAsync: ensureValidEmail } = useMutation({
    mutationFn: async () => {
      try {
        if (!resume.email) {
          return false
        }
        setEmailValidationStatus(FetchStatuses.loading)
        const { message } = await validateEmail(resume.email, { allowAvailableOnly: true })
        setEmailValidationStatus(message ? FetchStatuses.failed : FetchStatuses.loaded)
        setBackendValidationMessage(message || '')
        return !message
      } catch (error) {
        setEmailValidationStatus(FetchStatuses.failed)
        setBackendValidationMessage(UNKNOWN_ERROR_MESSAGE)
        return false
      }
    },
  })

  useEffect(() => () => abortControllerRef.current.abort(), [])

  useEffect(() => {
    setBackendValidationMessage('')
    setShowEmailRequiredError(0)
    setEmailValidationStatus(resume.email ? FetchStatuses.loading : FetchStatuses.notAsked)
  }, [resume.email, setShowEmailRequiredError])

  useDebouncedEffect(
    () => {
      ensureValidEmail()
      return () => abortControllerRef.current.abort()
    },
    2000,
    [resume.email],
  )

  useEffect(() => {
    trackInternalEvent('visit_resume_editor', {
      step: 'details',
      editor: 'step_by_step',
    })
  }, [])

  const handleSignUpWithResumeData = async () => {
    trackInternalEvent('click_next_step', {
      label: 'step_builder',
      step: snakeCase(SectionNames.details),
    })

    if (!resume.email) {
      toggleEmailRequiredError()
      return
    }

    if (
      [FetchStatuses.loading, FetchStatuses.notAsked, FetchStatuses.failed].includes(
        emailValidationStatus,
      )
    ) {
      setShouldFocusEmailField(Math.random() + 1)
      return
    }

    setIsSigningUp(true)

    try {
      const { recaptchaToken, recaptchaAction } = await getRecaptchaLoginActionToken()
      const { data } = await apiClient.post(`/sign-up/register`, {
        recaptchaToken,
        recaptchaAction,
        signUp: {
          template: resume.template,
          firstName: resume.firstName,
          lastName: resume.lastName,
          email: resume.email,
          phone: resume.phoneNumber,
          confirmEmail: isConsentChecked,
          jobTitle: resume.position,
          resumeName: resume.name,
          countryName: resume.countryName,
          city: resume.city,
          address: resume.address,
          postalCode: resume.postalCode,
          drivingLicense: resume.drivingLicense,
          birthPlace: resume.birthPlace,
          birthDate: resume.birthDate,
          nationality: resume.nationality,
          showPrefillResumeModal: false,
          resumeData: { stepByStepFlow: { currentStep: SectionNames.workExperiences } },
          templateSettings: resume.templateSettings,
          funnel: 'Stepper',
        },
      })

      if (data.success) {
        resetResume()
        navigate(`/resumes/${data.firstDocumentId}/edit?${UNBLOCKED_EDITOR_URL_PARAM}`, {
          replace: true,
        })
        trackMarketingEvent('Sign up', 'Complete Registration')
        trackInternalEvent('submit_marketing_consent', {
          website_host: window.location.host,
          device: isPhone ? 'mobile' : 'desktop',
          funnel: 'stepper',
        })
      } else {
        setIsSigningUp(false)
        setBackendValidationMessage(UNKNOWN_ERROR_MESSAGE)
      }
    } catch (error) {
      ErrorLogger.log(error)
      setIsSigningUp(false)
      setBackendValidationMessage(UNKNOWN_ERROR_MESSAGE)
    }
  }

  const handleSuggestionClick = (suggestion: ResumeFieldSuggest) => {
    if (suggestion.type === 'simpleField' && suggestion.fieldId) {
      FocusManager.focus(suggestion.fieldId)
    }
  }

  return (
    <Container>
      <ResumeScoreContainer
        points={resumeScore}
        suggestion={scoreImprovementSuggestion}
        handleSuggestionClick={handleSuggestionClick}
      />
      <MainContentContainer $autoTailorJob={isAutoTailoringFromPublicJobBoard}>
        <ContentSectionWrapper $autoTailorJob={isAutoTailoringFromPublicJobBoard}>
          <PersonalDetailsSectionGuest
            backendValidationMessage={backendValidationMessage}
            emailValidationStatus={emailValidationStatus}
            isConsentChecked={isConsentChecked}
            setIsConsentChecked={setIsConsentChecked}
          />
        </ContentSectionWrapper>
      </MainContentContainer>
      <NavigationSection
        currentIndex={0}
        nextButtonText={nextButtonText}
        isBackButtonPassive
        handleNext={handleSignUpWithResumeData}
        isNextActionInProgress={isSigningUp}
      >
        <GuestCopyrightContainer>
          <GuestCopyrightContent dangerouslySetInnerHTML={{ __html: copyrightMarkup }} />
        </GuestCopyrightContainer>
      </NavigationSection>
    </Container>
  )
}
